/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import { FormLabelsDto, LabelFormType } from '@/components/contexts/LabelsContext/LabelsProvider';
import { getWorkplanFormIdForLabelFormType } from '@/components/contexts/LabelsContext/useLabels';

import React from 'react';
import { pdf } from '@react-pdf/renderer';

import WorkplanPDF from '@/wpt-lib/pdf/WorkplanPDF';
import { DataContext, PDFData } from '@/wpt-lib/pdf/useData';
import { AxiosInstance, AxiosResponse } from 'axios';
import { mapWorkplanStateToString, Workplan } from '@/wpt-lib/interfaces/Workplan';
import { merge } from 'lodash';
import { makeAllocationChart } from '@/wpt-lib/pdf/charts';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';
import { LeaveFormResponse } from '@/wpt-lib/interfaces/Leave';
import { FormChange } from '@/wpt-lib/interfaces/EntityChange';

interface ListRequest {
    workplan: number;
    pageSize: number;
    orderByOrientation: 'ASC' | 'DESC';
    orderByColumn: string;
}

export interface LeaveWithDays extends Omit<LeaveFormResponse, keyof FormChange> {
    days: number;
}

async function requestWorkPlanData(
    workplan: Workplan,
    version: number,
    axios: AxiosInstance,
    requestorAssignment: Assignment,
): Promise<PDFData> {
    const formRequests: [string, Partial<ListRequest>][] = [
        ['/leave', { orderByColumn: 'startDate' }],
        ['/career-goal', {}],
        ['/activity', {}],
        ['/health-and-safety-training', {}],
        ['/consideration', {}],
    ];

    const definitionRequests: [LabelFormType, string[]][] = [
        ['LEAVE', ['absenceType', 'leaveCode']],
        [
            'ACTIVITY',
            [
                '2027Strategy',
                'academicFocusArea',
                'teachingExpectationsAreas',
                'researchExpectationsAreas',
                'serviceExpectationsAreas',
                'projectType',
                'targetIncomeCategory',
                'newResearchProjectLeadResearcher',
                'publicationType',
                'collaborating',
                'subjectActivities',
                'otherTeachingActivities',
                'teachingSubjectCourseType',
                'teachingMicrocredentialType',
                'teachingEducational',
                'teachingShortCourseType',
                'researchScholarship',
                'newProjectInternalExternalSeedFunded',
                'publicationBookStatus',
                'conferenceLevel',
                'researchCollaborationType',
                'serviceEngagement',
            ],
        ],
        ['CONSIDERATION', ['disclosureDeclaration', 'pepConfirmed']],
        [
            'PERSONAL_GOAL',
            [
                'serviceOrEngagement',
                'formalLeadershipRole',
                'internalOrExternalGoalLeadership',
                'internalOrExternalGoalService',
                'publicationType',
                'yes-no-unsure',
                'targetIncomeCategory',
            ],
        ],
        ['BEHAVIOURAL', ['behaviouralKpi']],
    ];

    if (!workplan.assignment) throw new Error('Assignment not stored in workplan');

    /* Reload assignment to make sure all references are loaded, user, organisation... */
    const assignment = (await axios.get(`/assignment/${workplan.assignment.id}`)).data;

    const responses = await Promise.all([
        ...formRequests.map(([url, params]) =>
            axios.get(url, {
                params: {
                    workplan: workplan.id,
                    version,
                    pageSize: 1000,
                    orderByOrientation: 'ASC',
                    ...params,
                },
            }),
        ),
        axios.get(`/workplan/${workplan.id}`, {
            params: { assignment: requestorAssignment.id, version },
        }),
        axios.get(`/workplan/${workplan.id}/version/${version}`),
        axios.get(`/workplan/${workplan.id}/comment`, {
            params: { assignment: requestorAssignment.id, version, all: true },
        }),
        axios.get(`/workplan/${workplan.id}/history`),
        axios.get(`/workplan/${workplan.id}/summary`, { params: { version } }),
        axios.get(`/leave/booked`, {
            params: { workplan: workplan.id, version },
        }),
        axios.get(`/workplan/${workplan.id}/${version}/files`, {
            params: { assignment: requestorAssignment.id },
        }),
        ...definitionRequests.map(([formType, properties]) =>
            getWorkplanFormIdForLabelFormType(formType, workplan)
                ? axios.get<FormLabelsDto>(`/forms/labels/${getWorkplanFormIdForLabelFormType(formType, workplan)}`, {
                      params: {
                          properties: properties.join(','),
                      },
                  })
                : Promise.resolve({
                      data: {
                          formId: 0,
                          labels: {
                              [formType]: {},
                          },
                      },
                  }),
        ),
    ]);

    const labels = responses.slice(10).map((r: Partial<AxiosResponse<FormLabelsDto>>) => r?.data?.labels);

    responses[0].data.items = await Promise.all(
        responses[0].data.items.map(async (leave: Omit<LeaveFormResponse, keyof FormChange>) => {
            const days = (
                await axios.get(`/holiday/business-days`, {
                    params: { startDate: leave.startDate, endDate: leave.endDate },
                })
            ).data;
            return { ...leave, days };
        }),
    );

    return {
        user: assignment.user,
        assignment,
        leaves: responses[0].data.items,
        goals: responses[1].data.items,
        activities: responses[2].data.items,
        training: responses[3].data.items,
        considerations: responses[4].data.items,
        workplan: responses[5].data,
        version: responses[6].data,
        comments: responses[7].data,
        stateHistory: responses[8].data,
        summary: responses[9].data,
        bookedLeaves: responses[10].data,
        workplanAttachments: responses[11].data,
        labels: merge(labels[0], ...labels.slice(1)),
    };
}

export async function exportWorkplan(
    workplan: Workplan,
    version: number,
    axios: AxiosInstance,
    requestorAssignment: Assignment,
): Promise<void> {
    const pdfData = await requestWorkPlanData(workplan, version, axios, requestorAssignment);

    const charts = await Promise.all([makeAllocationChart(pdfData)]);

    const blob = await pdf(
        <DataContext.Provider value={pdfData}>
            <WorkplanPDF charts={charts} />
        </DataContext.Provider>,
    ).toBlob();

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    a.target = '_blank';

    a.download = `Workplan_${workplan.year}_V${version}_${pdfData.user.staffId}_${
        mapWorkplanStateToString[workplan.currentVersion.state]
    }.pdf`;

    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
        URL.revokeObjectURL(url);
        document.body.removeChild(a);
    }, 500);
}
